

import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { NumberInputRange } from '~src/settings/general-settings/utils';

import { Messages } from '../../../../../codegen/Localize';

export const MAX_AUTO_SUSPENSION_FAILURE_LIMIT = NumberInputRange.LONG.MAX;
export const DEFAULT_AUTO_SUSPENSION_FAILURES_LIMIT_FOR_DAY_TIME_PERIOD = 10;
export const DEFAULT_AUTO_SUSPENSION_FAILURES_LIMIT_FOR_WEEK_TIME_PERIOD = 50;
export const DEFAULT_AUTO_SUSPENSION_FAILURES_LIMIT_FOR_MONTH_TIME_PERIOD = 100;

export const MAX_AUTO_SUSPENSION_ACCELERATION_IMPROVEMENT_THRESHOLD = 100;
export const DEFAULT_AUTO_SUSPENSION_ACCELERATION_IMPROVEMENT_THRESHOLD = 30;

export const MAX_AUTO_SUSPENSION_VIEWS_LIMIT = NumberInputRange.LONG.MAX;
export const DEFAULT_AUTO_SUSPENSION_VIEWS_LIMIT_FOR_DAY_TIME_PERIOD = 50;
export const DEFAULT_AUTO_SUSPENSION_VIEWS_LIMIT_FOR_WEEK_TIME_PERIOD = 300;
export const DEFAULT_AUTO_SUSPENSION_VIEWS_LIMIT_FOR_MONTH_TIME_PERIOD = 700;

export const enum TimePeriodType {
  Day = 'Day',
  Week = 'Week',
  Month = 'Month'
}

// Using data freshness policy strings here to refer to different time periods
// Also, this will not have localization issues as these strings are available from a longtime
export const getTimePeriodOptions = () => {
  const results = [];
  results.push({
    value: TimePeriodType.Day,
    content: getLocalizedTimePeriod(TimePeriodType.Day),
  });
  results.push({
    value: TimePeriodType.Week,
    content: getLocalizedTimePeriod(TimePeriodType.Week),
  });
  results.push({
    value: TimePeriodType.Month,
    content: getLocalizedTimePeriod(TimePeriodType.Month),
  });
  return results;
};


/** Check if a given value is a valid input for auto suspension failure limit to be set */
export const autoSuspendFailuresLimitFieldValidator = (autoSuspendFailureLimit: number) => {
  if (!isValidNumber(autoSuspendFailureLimit)) {
    return Messages.autoSuspensionFailureLimitMustBeWholeNumber();
  }

  if (autoSuspendFailureLimit > MAX_AUTO_SUSPENSION_FAILURE_LIMIT) {
    return Messages.autoSuspensionFailureLimitMustBeLessThanMax();
  }

  return null;
};

export const autoSuspendAccelerationImprovementThresholdFieldValidator = (accelerationImprovementThreshold: number) => {
  if(!isValidAutoSuspensionAccelerationImprovementThreshold(accelerationImprovementThreshold)){
    return Messages.autoSuspensionAccelerationImprovementThresholdMustBeBetweenZeroAndHundred();
  }
  return null;
};

/** Check if a given value is a valid input for auto suspension failure limit to be set */
export const autoSuspendViewsLimitFieldValidator = (autoSuspendViewsLimit: number) => {
  if (!isValidNumber(autoSuspendViewsLimit)) {
    return Messages.autoSuspensionViewsLimitMustBeWholeNumber();
  }

  if (autoSuspendViewsLimit > MAX_AUTO_SUSPENSION_VIEWS_LIMIT) {
    return Messages.autoSuspensionViewsLimitMustBeLessThanMax();
  }

  return null;
};

export const getDefaultAutoSuspensionFailureLimitValue = (timePeriod: string) => {
  switch (timePeriod) {
    case TimePeriodType.Day:
      return DEFAULT_AUTO_SUSPENSION_FAILURES_LIMIT_FOR_DAY_TIME_PERIOD;
    case TimePeriodType.Week:
        return DEFAULT_AUTO_SUSPENSION_FAILURES_LIMIT_FOR_WEEK_TIME_PERIOD;
    case TimePeriodType.Month:
      return DEFAULT_AUTO_SUSPENSION_FAILURES_LIMIT_FOR_MONTH_TIME_PERIOD;
  }
  return DEFAULT_AUTO_SUSPENSION_FAILURES_LIMIT_FOR_DAY_TIME_PERIOD;
};

export const getLocalizedTimePeriod = (timePeriod: string) => {
  switch (timePeriod) {
    case TimePeriodType.Day:
      return Messages.dataFreshness_RecurrenceType_Day();
    case TimePeriodType.Week:
        return Messages.dataFreshness_RecurrenceType_Week();
    case TimePeriodType.Month:
      return Messages.dataFreshness_RecurrenceType_Month();
  }
  return Messages.dataFreshness_RecurrenceType_Week();
};

export const getDefaultAutoSuspensionViewsLimitValue = (timePeriod: string) => {
  switch (timePeriod) {
    case TimePeriodType.Day:
      return DEFAULT_AUTO_SUSPENSION_VIEWS_LIMIT_FOR_DAY_TIME_PERIOD;
    case TimePeriodType.Week:
        return DEFAULT_AUTO_SUSPENSION_VIEWS_LIMIT_FOR_WEEK_TIME_PERIOD;
    case TimePeriodType.Month:
      return DEFAULT_AUTO_SUSPENSION_VIEWS_LIMIT_FOR_MONTH_TIME_PERIOD;
  }
  return DEFAULT_AUTO_SUSPENSION_VIEWS_LIMIT_FOR_DAY_TIME_PERIOD;
};

export const getDefaultAccelerationImprovementThresholdValue = () => {
  return DEFAULT_AUTO_SUSPENSION_ACCELERATION_IMPROVEMENT_THRESHOLD;
};

export const isTableauOnPrem = () => {
  const sessionManagementService = getSessionManagementServiceInstance();
  return !sessionManagementService.isOnlineEnvironment();
};


function isValidNumber(value: number) {
  return !isNaN(value) && value >= 0 && isFinite(value);
}

function isValidAutoSuspensionAccelerationImprovementThreshold(value: number) {
  return !isNaN(value) && value >= 0 && isFinite(value) && value <= 100;
}